import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  AlertCircle,
  Hash,
  Type,
  Layers,
  CheckCircle2
} from 'lucide-react';
import { motion } from 'motion/react';
import { AccountingAccount, AccountingCategory, Account, PlannedConcept } from '../../types';
import { createAccountingAccount, updateAccountingAccount } from '../../services/accountingService';

interface AccountFormModalProps {
  onClose: () => void;
  userId: string;
  accounts: AccountingAccount[];
  financeAccounts: Account[];
  plannedConcepts: PlannedConcept[];
  editingAccount?: AccountingAccount;
}

export const AccountFormModal: React.FC<AccountFormModalProps> = ({ 
  onClose, 
  userId, 
  accounts, 
  financeAccounts,
  plannedConcepts,
  editingAccount 
}) => {
  const [name, setName] = useState(editingAccount?.name || '');
  const [code, setCode] = useState(editingAccount?.code || '');
  const [category, setCategory] = useState<AccountingCategory>(editingAccount?.category || 'ACTIVO');
  const [parentId, setParentId] = useState<string | null>(editingAccount?.parentId || null);
  const [isSelectable, setIsSelectable] = useState(editingAccount?.isSelectable ?? true);
  const [initialBalance, setInitialBalance] = useState(editingAccount?.initialBalance?.toString() || '0');
  const [linkedFinanceAccountId, setLinkedFinanceAccountId] = useState(editingAccount?.linkedFinanceAccountId || '');
  const [linkedPlannedConceptId, setLinkedPlannedConceptId] = useState(editingAccount?.linkedPlannedConceptId || '');
  const [loading, setLoading] = useState(false);

  // Auto-generate suggest code when parent changes
  useEffect(() => {
    if (parentId) {
      const parent = accounts.find(a => a.id === parentId);
      if (parent) {
        setCategory(parent.category);
        
        // Auto generate code only if NOT editing
        if (!editingAccount) {
          const siblings = accounts.filter(a => a.parentId === parentId).sort((a,b) => b.code.localeCompare(a.code));
          if (siblings.length > 0) {
            const lastCode = siblings[0].code;
            const parts = lastCode.split('.');
            const lastPart = parseInt(parts[parts.length - 1]);
            if (!isNaN(lastPart)) {
              parts[parts.length - 1] = (lastPart + 1).toString().padStart(parts[parts.length - 1].length, '0');
              setCode(parts.join('.'));
            }
          } else {
            setCode(`${parent.code}.01`);
          }
        }
      }
    }
  }, [parentId, accounts, editingAccount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !code) return;

    setLoading(true);
    try {
      const parent = parentId ? accounts.find(a => a.id === parentId) : null;
      const level = parent ? (parent.level + 1) : 1;

      const data: any = {
        name,
        code,
        category: parent ? parent.category : category,
        parentId,
        isSelectable,
        level,
        initialBalance: parseFloat(initialBalance) || 0,
        linkedFinanceAccountId: linkedFinanceAccountId || null,
        linkedPlannedConceptId: linkedPlannedConceptId || null,
      };

      if (editingAccount) {
        await updateAccountingAccount(userId, editingAccount.id, data);
      } else {
        await createAccountingAccount(userId, data);
      }
      onClose();
    } catch (error) {
      console.error(error);
      alert("Error al guardar la cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl w-full max-w-lg shadow-2xl overflow-y-auto max-h-[90vh]"
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta Contable'}
            </h2>
            <p className="text-zinc-500 text-sm">Gestiona la estructura de tu plan de cuentas.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-xl transition-colors">
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Código</label>
              <div className="relative">
                <Hash className="absolute left-3 top-3 text-zinc-600" size={16} />
                <input 
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Ej: 1.1.01"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Categoría</label>
              <select 
                value={category}
                onChange={(e) => setCategory(e.target.value as AccountingCategory)}
                disabled={!!parentId}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors disabled:opacity-50"
              >
                <option value="ACTIVO">ACTIVO</option>
                <option value="PASIVO">PASIVO</option>
                <option value="PATRIMONIO">PATRIMONIO</option>
                <option value="INGRESO">INGRESO</option>
                <option value="EGRESO">EGRESO</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Nombre de la Cuenta</label>
            <div className="relative">
              <Type className="absolute left-3 top-3 text-zinc-600" size={16} />
              <input 
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                placeholder="Nombre descriptivo"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Cuenta Padre (Opcional)</label>
            <div className="relative">
              <Layers className="absolute left-3 top-3 text-zinc-600" size={16} />
              <select 
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value || null)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
              >
                <option value="">-- Sin Padre (Nivel Raíz) --</option>
                {accounts.filter(a => a.id !== editingAccount?.id).sort((a,b) => a.code.localeCompare(b.code)).map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Saldo Inicial</label>
              <input 
                type="number"
                step="any"
                value={initialBalance}
                onChange={(e) => setInitialBalance(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
            <div className="flex flex-col justify-center">
              <label className="flex items-center gap-2 cursor-pointer group mt-4">
                <div className={`w-10 h-6 rounded-full transition-colors relative ${isSelectable ? 'bg-indigo-600' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isSelectable ? 'left-5' : 'left-1'}`} />
                </div>
                <input 
                  type="checkbox" 
                  checked={isSelectable} 
                  onChange={(e) => setIsSelectable(e.target.checked)} 
                  className="hidden" 
                />
                <span className="text-xs text-zinc-400 group-hover:text-white transition-colors">Imputable (Recibe Asientos)</span>
              </label>
            </div>
          </div>

          <div className="bg-zinc-800/50 border border-zinc-800 p-4 rounded-3xl space-y-4">
            <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Vínculos de Integración (Opcional)</h4>
            
            <div className="space-y-2">
              <label className="text-xs text-zinc-400 pl-1">Cuenta del Módulo Financiero</label>
              <select 
                value={linkedFinanceAccountId}
                onChange={(e) => setLinkedFinanceAccountId(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
              >
                <option value="">-- No vincular a cuenta --</option>
                <optgroup label="Bancos y Efectivo">
                  {financeAccounts.filter(a => a.type !== 'TARJETA DE CREDITO').map(fa => (
                    <option key={fa.id} value={fa.id}>{fa.name} (Saldo: {new Intl.NumberFormat('es-PY').format(fa.balance)})</option>
                  ))}
                </optgroup>
                <optgroup label="Tarjetas de Crédito">
                  {financeAccounts.filter(a => a.type === 'TARJETA DE CREDITO').map(fa => (
                    <option key={fa.id} value={fa.id}>{fa.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-zinc-400 pl-1">Concepto de Planificación</label>
              <select 
                value={linkedPlannedConceptId}
                onChange={(e) => setLinkedPlannedConceptId(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
              >
                <option value="">-- No vincular a concepto --</option>
                <optgroup label="Ingresos">
                  {plannedConcepts.filter(c => c.type === 'INCOME').map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </optgroup>
                <optgroup label="Gastos">
                  {plannedConcepts.filter(c => c.type === 'EXPENSE').map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          {isSelectable && parentId && accounts.find(a => a.id === parentId)?.isSelectable && (
            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-2xl flex gap-3 text-amber-500">
              <AlertCircle size={20} className="shrink-0" />
              <p className="text-[10px] leading-relaxed">
                Aviso: El padre también es imputable. Se recomienda que solo las cuentas "hoja" reciban movimientos.
              </p>
            </div>
          )}

          <div className="pt-6 border-t border-zinc-800 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl font-bold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Guardando...' : 'Guardar Cuenta'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
